// Pure access-control helpers (see `features/users-permissions.md`). This is
// the single source of truth for the permission catalog and the predefined
// roles; the `users_permissions` migration and the server's boot seeding both
// mirror it. Framework-free and side-effect-free, shared by the client (to
// render a permission-aware UI) and the server (to enforce it).

import type { PermissionCode, PermissionInfo, RoleInfo } from './types';

/** Max length accepted for a user's display name. */
export const USER_NAME_MAX = 120;
/** Max length accepted for a user's email address. */
export const USER_EMAIL_MAX = 254;
/** Minimum length accepted for a password. */
export const PASSWORD_MIN = 8;

/** Role code of the predefined Super Admin role (all permissions, all companies). */
export const SUPER_ADMIN_ROLE_ID = 'SUPER_ADMIN';
/** Role code of the predefined Company Admin role (manages linked companies). */
export const COMPANY_ADMIN_ROLE_ID = 'COMPANY_ADMIN';
/**
 * Role code of the predefined Coach role: adds and edits the club's athletes
 * and invites them to sign in — the whole athlete-onboarding flow — and
 * nothing else. Tournaments, photos, company profile and user management stay
 * with Company Admins, so a club only ever hands out two roles (Coach and
 * Company Admin), which keeps onboarding simple.
 */
export const COACH_ROLE_ID = 'COACH';
/**
 * Role code of the predefined Athlete role — an athlete's or guardian's own
 * login (see `features/athlete-access.md`). Its permission bundle is
 * deliberately **empty**: an athlete account is authorised entirely by its
 * `AthleteAccess` rows and holds no company links, so it cannot reach any
 * staff route even if a guard were missed. The role exists to label the account
 * type in the users admin and as the hook for future athlete-only permissions.
 */
export const ATHLETE_ROLE_ID = 'ATHLETE';

/**
 * The full permission catalog. Order here is display order in the management
 * UI. The `id` codes are stable — they are primary keys in Postgres and are
 * referenced by the route guards — so never rename one; add new entries instead.
 */
export const ALL_PERMISSIONS: readonly PermissionInfo[] = [
  {
    id: 'CREATE_TOURNAMENT',
    name: 'Create tournament',
    description: 'Create tournaments within accessible companies.',
  },
  {
    id: 'DELETE_TOURNAMENT',
    name: 'Delete tournament',
    description: 'Delete tournaments within accessible companies.',
  },
  {
    id: 'MANAGE_PHOTOS',
    name: 'Manage photos',
    description: 'Upload, caption and delete tournament photos.',
  },
  {
    id: 'CREATE_COMPANY',
    name: 'Create company',
    description: 'Create, rename and delete companies on the platform.',
  },
  {
    id: 'VERIFY_COMPANY',
    name: 'Verify company',
    description:
      'Review self-registered companies and mark them as verified on the platform.',
  },
  {
    id: 'EDIT_COMPANY',
    name: 'Edit company profile',
    description: 'Edit the profile (logo, description) of accessible companies.',
  },
  {
    id: 'CREATE_USER',
    name: 'Create user',
    description: 'Create platform users and assign their initial access.',
  },
  {
    id: 'DELETE_USER',
    name: 'Delete user',
    description:
      'Delete platform users. Platform-level: company managers unlink users from their companies instead.',
  },
  {
    id: 'EDIT_USER',
    name: 'Edit user',
    description: 'Edit users: profile, roles, permissions, companies, blocking.',
  },
  {
    id: 'MANAGE_ATHLETES',
    name: 'Manage athletes',
    description: 'Create, edit and delete athlete profiles of accessible companies.',
  },
  {
    id: 'MANAGE_ATHLETE_ACCESS',
    name: 'Manage athlete access',
    description:
      'Invite athletes and guardians to sign in, and revoke their access, within accessible companies.',
  },
  {
    id: 'MANAGE_TEAMS',
    name: 'Manage teams',
    description: 'Create, edit and delete teams and their members of accessible companies.',
  },
  {
    id: 'MANAGE_TRAINING_CLASSES',
    name: 'Manage training classes',
    description:
      'Create training classes, assign coaches, enrol athletes and track attendance of accessible companies.',
  },
] as const;

/** Every permission code, in catalog order. */
export const ALL_PERMISSION_IDS: readonly PermissionCode[] = ALL_PERMISSIONS.map(
  (p) => p.id,
);

/**
 * The predefined roles. Seeded by the `users_permissions` migration (later
 * ones add their own) and re-ensured (idempotently) at server boot, so fresh
 * stores behave identically to migrated ones. Custom roles can be added later;
 * nothing below assumes these are the only ones.
 */
export const PREDEFINED_ROLES: readonly RoleInfo[] = [
  {
    id: SUPER_ADMIN_ROLE_ID,
    name: 'Super Admin',
    description: 'Full access to everything on the platform, across all companies.',
    permissionIds: [...ALL_PERMISSION_IDS],
  },
  {
    id: COMPANY_ADMIN_ROLE_ID,
    name: 'Company Admin',
    description: 'Manages everything within their linked companies.',
    // No DELETE_USER: deleting an account is final and platform-wide, so it is
    // reserved for Super Admins. A Company Admin unlinks a user from their
    // companies (or blocks them) instead — the account itself stays.
    permissionIds: [
      'CREATE_TOURNAMENT',
      'DELETE_TOURNAMENT',
      'MANAGE_PHOTOS',
      'EDIT_COMPANY',
      'CREATE_USER',
      'EDIT_USER',
      'MANAGE_ATHLETES',
      'MANAGE_ATHLETE_ACCESS',
      'MANAGE_TEAMS',
      'MANAGE_TRAINING_CLASSES',
    ],
  },
  {
    id: COACH_ROLE_ID,
    name: 'Coach',
    description:
      "Adds and edits the club's athletes and coaches, and invites them to sign in. No tournament, company or user management — that stays with Company Admins.",
    permissionIds: ['MANAGE_ATHLETES', 'MANAGE_ATHLETE_ACCESS'],
  },
  {
    id: ATHLETE_ROLE_ID,
    name: 'Athlete',
    description:
      "An athlete's or guardian's own login. Grants no platform permissions — access to a profile comes from the athlete access grants themselves.",
    permissionIds: [],
  },
] as const;

/**
 * Permissions that grant platform-wide capabilities (they act across companies,
 * not within one). A company-scoped user manager may never grant these — nor
 * the Super Admin role — or they could escalate beyond their own companies.
 * `DELETE_USER` is here because deleting an account is final and reaches beyond
 * any one company; a company manager unlinks users from their companies instead.
 * `VERIFY_COMPANY` is here because verification is the platform's own review of
 * a self-registered company — a company could otherwise verify itself.
 */
export const PLATFORM_PERMISSION_IDS: readonly PermissionCode[] = [
  'CREATE_COMPANY',
  'VERIFY_COMPANY',
  'DELETE_USER',
];

/** Whether a string is one of the known permission codes. */
export function isPermissionCode(value: string): value is PermissionCode {
  return (ALL_PERMISSION_IDS as readonly string[]).includes(value);
}

/**
 * The roles a company-scoped manager is offered when assigning access. Excludes
 * the Super Admin role (platform-wide power the server refuses to a scoped
 * grantor anyway) and the Athlete role (assigned by the invite/claim flow,
 * never by hand). With today's catalog that leaves exactly Company Admin and
 * Coach — the two roles a club hands out, which keeps onboarding simple.
 * Presentation-side companion to the server's `guardScopedGrants`.
 */
export function companyGrantableRoles(roles: readonly RoleInfo[]): RoleInfo[] {
  return roles.filter(
    (role) => role.id !== SUPER_ADMIN_ROLE_ID && role.id !== ATHLETE_ROLE_ID,
  );
}

/** The subset of a user's grants that access decisions derive from. */
export interface AccessGrants {
  roleIds: string[];
  permissionIds: PermissionCode[];
  companyIds: number[];
}

/**
 * Whether the grants include the Super Admin role. Super Admins bypass
 * company scoping entirely (they act on every company).
 */
export function isSuperAdmin(grants: Pick<AccessGrants, 'roleIds'>): boolean {
  return grants.roleIds.includes(SUPER_ADMIN_ROLE_ID);
}

/**
 * The effective permission set: the union of every held role's permissions and
 * the direct per-user grants. Role ids that do not resolve against `roles`
 * (e.g. a role deleted after assignment) contribute nothing.
 */
export function effectivePermissions(
  grants: Pick<AccessGrants, 'roleIds' | 'permissionIds'>,
  roles: readonly RoleInfo[],
): Set<PermissionCode> {
  const byId = new Map(roles.map((role) => [role.id, role]));
  const result = new Set<PermissionCode>();
  for (const roleId of grants.roleIds) {
    for (const permission of byId.get(roleId)?.permissionIds ?? []) {
      result.add(permission);
    }
  }
  for (const permission of grants.permissionIds) {
    result.add(permission);
  }
  return result;
}

/**
 * Whether the grants allow acting on `companyId`: Super Admins act on every
 * company, everyone else only on their linked ones.
 */
export function canAccessCompany(
  grants: Pick<AccessGrants, 'roleIds' | 'companyIds'>,
  companyId: number,
): boolean {
  return isSuperAdmin(grants) || grants.companyIds.includes(companyId);
}

/**
 * Whether the permission set opens the user-management surface at all (the
 * Users screen is shown when the user can create, edit or delete users).
 */
export function canManageUsers(permissions: ReadonlySet<PermissionCode>): boolean {
  return (
    permissions.has('CREATE_USER') ||
    permissions.has('EDIT_USER') ||
    permissions.has('DELETE_USER')
  );
}

// Deliberately simple shape check (`local@domain.tld`); real validation happens
// by delivering mail, which is out of scope. Guards against obvious typos only.
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Whether a string looks like an email address (after trimming). */
export function isValidEmail(email: string): boolean {
  const trimmed = email.trim();
  return (
    trimmed.length > 0 && trimmed.length <= USER_EMAIL_MAX && EMAIL_PATTERN.test(trimmed)
  );
}

/** Normalize an email for storage and lookup (case-insensitive, trimmed). */
export function normalizeEmail(email: string): string {
  return email.trim().toLowerCase();
}
